import { useTranslation } from 'react-i18next';

import { Label, LABEL_TYPE } from '@/components/generic';
import { ArrowDownHeavyIcon, ArrowUpHeavyIcon } from '@/resources';
import { getTestId, TestIds } from '@/shared/test-ids';
import { EditableThemeText } from '@/types/CustomVideoThemeTypes';

type TextSectionProps = {
  text: EditableThemeText;
  onTimeChange: (textId: number, field: 'startTime' | 'endTime', value: string) => void;
};

export const TextSection = ({ text, onTimeChange }: TextSectionProps) => {
  const [t] = useTranslation('common');
  const translationKey = text.value.replace(/^\{/, '').replace(/\}$/, '');

  return (
    <div className='flex flex-1 flex-row items-center gap-4 rounded-lg border border-dashed border-primary-blue p-3 lg:mx-2'>
      <div className='flex items-center gap-2'>
        <Label text={t(translationKey)} type={LABEL_TYPE.PARAGRAPH3_BOLD} />
        <span className='text-secondary-gray'>-</span>
      </div>
      <div className='flex items-center gap-4'>
        <div className='flex flex-row items-center gap-3'>
          <Label text={t('fromSeconds')} type={LABEL_TYPE.PARAGRAPH3_BOLD} />
          <span
            data-testid={getTestId(TestIds.tributeVideo.themeTextTimingValue, `${translationKey}-start`)}
            className='text-sm font-semibold text-secondary-gray'>
            {text.startTime}s
          </span>
          <div className='flex flex-col'>
            <button
              type='button'
              data-testid={getTestId(TestIds.tributeVideo.themeTextTimingAdjustButton, `${translationKey}-start-increase`)}
              className='flex h-3 items-center justify-center rounded hover:bg-secondary-pale-blue-gray'
              onClick={() =>
                onTimeChange(text.id, 'startTime', String(Math.min(text.startTime + 1, 40)))
              }>
              <ArrowUpHeavyIcon className='size-3 text-primary-blue' />
            </button>
            <button
              type='button'
              data-testid={getTestId(TestIds.tributeVideo.themeTextTimingAdjustButton, `${translationKey}-start-decrease`)}
              className='flex h-3 items-center justify-center rounded hover:bg-secondary-pale-blue-gray'
              onClick={() =>
                onTimeChange(text.id, 'startTime', String(Math.max(text.startTime - 1, 0)))
              }>
              <ArrowDownHeavyIcon className='size-3 text-primary-blue' />
            </button>
          </div>
        </div>
        <div className='flex flex-row items-center gap-3'>
          <Label text={t('toSeconds')} type={LABEL_TYPE.PARAGRAPH3_BOLD} />
          <span
            data-testid={getTestId(TestIds.tributeVideo.themeTextTimingValue, `${translationKey}-end`)}
            className='text-sm font-semibold text-secondary-gray'>
            {text.endTime}s
          </span>
          <div className='flex flex-col'>
            <button
              type='button'
              data-testid={getTestId(TestIds.tributeVideo.themeTextTimingAdjustButton, `${translationKey}-end-increase`)}
              className='flex h-3 items-center justify-center rounded hover:bg-secondary-pale-blue-gray'
              onClick={() =>
                onTimeChange(text.id, 'endTime', String(Math.min(text.endTime + 1, 40)))
              }>
              <ArrowUpHeavyIcon className='size-3 text-primary-blue' />
            </button>
            <button
              type='button'
              data-testid={getTestId(TestIds.tributeVideo.themeTextTimingAdjustButton, `${translationKey}-end-decrease`)}
              className='flex h-3 items-center justify-center rounded hover:bg-secondary-pale-blue-gray'
              onClick={() =>
                onTimeChange(text.id, 'endTime', String(Math.max(text.endTime - 1, 0)))
              }>
              <ArrowDownHeavyIcon className='size-3 text-primary-blue' />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
